import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Send, Users, ChevronLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const AdminMessages = () => {
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const autoOpenedRef = useRef(false);
  const adminId = "admin";

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/admin/all-conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
      return res.data;
    } catch {
      toast.error("Failed to load conversations");
      return [];
    } finally {
      setLoadingConvs(false);
    }
  };

  useEffect(() => {
    loadConversations().then((convs) => {
      // Deep-link support: open a specific conversation when navigated here
      // from a student/tutor detail view (e.g. the "Message" button).
      const targetId = location.state?.openConversationId;
      if (targetId && !autoOpenedRef.current) {
        autoOpenedRef.current = true;
        const target = convs.find((c) => c.id === targetId);
        if (target) openConversation(target);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setLoadingMsgs(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/messages/conversations/${conv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages);
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || !activeConv) return;
    setSending(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/messages/conversations/${activeConv.id}/send`, { content: text }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInput("");
      // Reload messages
      const res = await axios.get(`${API}/messages/conversations/${activeConv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages);
    } catch {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const participantSummary = (conv) => {
    return Object.values(conv.participant_names || {}).join(" & ");
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2" data-testid="admin-messages-title">Messages</h1>
      <p className="text-[#4A4A4A] mb-6">View and reply to all student–tutor conversations.</p>

      <div className="flex gap-6 min-h-[500px]">
        {/* Conversation list */}
        <div className="w-72 flex-shrink-0 brutalist-card overflow-hidden">
          <div className="p-4 border-b-2 border-[#0A0A0A]/10 bg-[#F0EFEB]">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <Users className="w-4 h-4" /> All Threads ({conversations.length})
            </h3>
          </div>
          <div className="overflow-y-auto max-h-[440px]">
            {loadingConvs ? (
              <p className="text-sm text-[#4A4A4A] p-4">Loading...</p>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center">
                <MessageCircle className="w-8 h-8 mx-auto text-[#4A4A4A] mb-2" />
                <p className="text-sm text-[#4A4A4A]">No conversations yet.</p>
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => openConversation(conv)}
                  className={`w-full text-left p-3 border-b border-[#0A0A0A]/5 hover:bg-[#F0EFEB] transition-colors ${activeConv?.id === conv.id ? "bg-[#D6E6FA]" : ""}`}
                  data-testid={`admin-conv-${conv.id}`}
                >
                  <div className="font-bold text-sm truncate">{participantSummary(conv)}</div>
                  <p className="text-xs text-[#4A4A4A] truncate mt-0.5">{conv.last_message_preview || "No messages yet"}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-[#4A4A4A]">{timeAgo(conv.last_message_at)}</span>
                    <span className="text-xs text-[#4A4A4A]">{conv.total_messages} msgs</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Thread view */}
        <div className="flex-1 brutalist-card overflow-hidden flex flex-col">
          {!activeConv ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageCircle className="w-12 h-12 mx-auto text-[#4A4A4A] mb-3" />
                <p className="text-[#4A4A4A]">Select a conversation to view its messages.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 border-b-2 border-[#0A0A0A]/10 bg-[#F0EFEB] flex items-center gap-3">
                <button onClick={() => setActiveConv(null)} className="p-1 hover:bg-[#D6E6FA] rounded md:hidden">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="w-9 h-9 rounded-full bg-[#3C8DE4] text-white flex items-center justify-center font-bold text-sm">
                  {Object.values(activeConv.participant_names || {})[0]?.charAt(0) || "?"}
                </div>
                <div>
                  <div className="font-bold text-sm">{participantSummary(activeConv)}</div>
                  <div className="text-xs text-[#4A4A4A] capitalize">
                    {Object.values(activeConv.participant_roles || {}).join(" · ")}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FDFBF7]">
                {loadingMsgs ? (
                  <p className="text-sm text-center text-[#4A4A4A]">Loading...</p>
                ) : messages.length === 0 ? (
                  <p className="text-sm text-center text-[#4A4A4A] italic py-4">No messages yet.</p>
                ) : (
                  messages.map(m => (
                    <div key={m.id} className="flex items-start gap-2" data-testid={`admin-message-${m.id}`}>
                      <div className="w-7 h-7 rounded-full bg-[#3C8DE4]/20 text-[#3C8DE4] flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">
                        {m.sender_name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span className="font-bold text-xs">{m.sender_name}</span>
                          <span className="text-xs text-[#4A4A4A]">{timeAgo(m.created_at)}</span>
                        </div>
                        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl rounded-tl-sm px-3 py-2 text-sm">
                          {m.content}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={bottomRef} />
              </div>

              <div className="p-3 border-t-2 border-[#0A0A0A]/10 bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
                    placeholder="Reply as admin..."
                    className="flex-1 brutalist-input text-sm py-2"
                    data-testid="admin-message-input"
                    disabled={sending}
                  />
                  <button
                    onClick={send}
                    disabled={sending || !input.trim()}
                    className="brutalist-btn-primary px-3 py-2 flex items-center gap-1 text-sm"
                    data-testid="admin-send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
